import { useEffect, useState } from "react";
import { useLantern, useSession } from "../hooks";
import type { Capability, JSONSchema, Task, TaskEvent } from "../sync/types";
import { IconTask } from "./icons";
import { SchemaForm } from "./SchemaForm";
import { outputFields } from "../taskOutput";
import { locale, t, tc } from "../i18n";

const stateLabel = (): Record<Task["state"], string> => ({
  queued: t("Queued"),
  claimed: t("Starting"),
  in_progress: t("In progress"),
  input_required: t("Needs input"),
  waiting_children: t("Waiting on subtasks"),
  completed: tc("state", "Done"),
  failed: t("Failed"),
  cancelled: t("Cancelled"),
  dead_lettered: t("Stuck"),
});

// The words for what happened to a task, in its history; any other is shown
// as it is.
const eventLabel = (type: string): string =>
  ({
    created: t("created"),
    claimed: t("claimed"),
    started: t("started"),
    reassigned: t("reassigned"),
    requeued: t("requeued"),
    retried: t("retried"),
    lease_revoked: t("lease revoked"),
    input_requested: t("input requested"),
    input_received: t("input received"),
    child_created: t("child created"),
    awaiting_children: t("awaiting children"),
    join_decided: t("join decided"),
    completed: t("completed"),
    failed: t("failed"),
    cancelled: t("cancelled"),
    dead_lettered: t("dead lettered"),
  })[type] ?? type.replace(/_/g, " ");

const terminal = new Set(["completed", "failed", "cancelled", "dead_lettered"]);

// TaskCard shows a task where people see it: on its thread's root message
// and in the task list (tasks spec section 11). It offers what the viewer
// can do: accept and complete work assigned to them, answer an input
// request, and cancel or retry their own tasks.
export function TaskCard({ id }: { id: string }) {
  const session = useSession();
  const state = useLantern();
  const task = state.tasks.get(id);
  const me = state.me?.id;
  const [cap, setCap] = useState<Capability>();
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<TaskEvent[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!session.store.getSnapshot().tasks.has(id)) void session.engine.loadTask(id);
  }, [id, session]);
  useEffect(() => {
    if (task?.capability) void session.capability(task.capability).then(setCap);
  }, [task?.capability, session]);
  useEffect(() => {
    if (open) void session.api.taskEvents(id).then((r) => setHistory(r.events), () => {});
  }, [open, id, session, task?.updated_at]);

  if (!task) return <div className="task-card loading">{t("Loading task…")}</div>;

  const act = async (fn: () => Promise<unknown>) => {
    setError("");
    try {
      await fn();
      await session.engine.loadTask(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };
  const mineToDo = task.assignee === me;
  const holding = task.lease?.holder_id === me;
  const requester = task.requester.kind === "principal" && task.requester.id === me;
  const responder = task.state === "input_required" && task.input_request?.responder_id === me;
  const p = task.progress;
  // Workflow approvals and forms carry their prompt, and a form its schema.
  const input = (task.input ?? {}) as { prompt?: string; schema?: JSONSchema };
  const outputSchema = task.capability === "lantern.form/v1" ? (input.schema ?? null) : (cap?.output_schema ?? null);

  return (
    <div className={`task-card state-${task.state}`} aria-label={t("Task {capability}", { capability: task.capability })} data-task={task.id}>
      <header>
        <span className="task-icon" aria-hidden>
          <IconTask size={16} />
        </span>
        <strong className="task-name">{task.capability}</strong>
        <span className="task-state">{stateLabel()[task.state] ?? task.state}</span>
      </header>
      <p className="task-meta">
        {task.requester.kind === "principal" && t("Asked by {name}", { name: session.name(task.requester.id) })}
        {task.assignee && <> · {tc("task", "for {name}", { name: session.name(task.assignee) })}</>}
        {task.lease && task.lease.holder_id !== task.assignee && <> · {t("worked by {name}", { name: session.name(task.lease.holder_id) })}</>}
        {task.deadline && <> · {t("due {time}", { time: new Date(task.deadline).toLocaleString(locale) })}</>}
      </p>
      {input.prompt && task.capability.startsWith("lantern.") && <p className="task-prompt">{input.prompt}</p>}
      {p && (
        <div className="task-progress">
          {p.total ? <progress max={p.total} value={p.done ?? 0} aria-label={t("Progress")} /> : null}
          {p.note && <span>{p.note}</span>}
        </div>
      )}
      {task.children.total > 0 && (
        <p className="task-children">
          {t("{done} of {total} subtasks done", { done: task.children.completed, total: task.children.total })}
          {task.children.failed > 0 && t(", {n} failed", { n: task.children.failed })}
          {task.children.open > 0 && t(", {n} open", { n: task.children.open })}
        </p>
      )}
      {task.error && task.state !== "completed" && (
        <p className="task-error">
          {task.error.code}
          {task.error.message && `: ${task.error.message}`}
        </p>
      )}
      {task.state === "completed" && task.output != null && <TaskOutput schema={outputSchema} output={task.output} />}
      {responder && task.input_request && (
        <div className="task-input">
          <p>{task.input_request.prompt}</p>
          <SchemaForm schema={task.input_request.schema} submit={t("Send")} onSubmit={(v) => act(() => session.api.provideInput(id, v))} />
        </div>
      )}
      {mineToDo && task.state === "queued" && <button onClick={() => act(() => session.api.acceptTask(id))}>{t("Accept")}</button>}
      {holding && task.lease && task.state === "in_progress" && (
        <div className="task-complete">
          <SchemaForm
            schema={outputSchema}
            submit={task.capability === "lantern.approval/v1" ? t("Decide") : t("Complete")}
            onSubmit={(v) => act(() => session.api.completeTask(id, task.lease!.epoch, v))}
            extra={
              <button type="button" className="secondary" onClick={() => act(() => session.api.failTask(id, task.lease!.epoch, "declined", "declined by assignee"))}>
                {t("Give up")}
              </button>
            }
          />
        </div>
      )}
      <div className="task-actions">
        {requester && !terminal.has(task.state) && (
          <button className="secondary" onClick={() => act(() => session.api.cancelTask(id, "cancelled by requester"))}>
            {t("Cancel")}
          </button>
        )}
        {requester && (task.state === "failed" || task.state === "dead_lettered") && (
          <button onClick={() => act(() => session.api.retryTask(id))}>{t("Retry")}</button>
        )}
        <button className="link" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? t("Hide details") : t("Details")}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      {open && (
        <div className="task-details">
          <h4>{t("Input")}</h4>
          <pre>{JSON.stringify(task.input, null, 2)}</pre>
          {task.output != null && (
            <>
              <h4>{t("Output")}</h4>
              <pre>{JSON.stringify(task.output, null, 2)}</pre>
            </>
          )}
          <h4>{t("History")}</h4>
          <ol className="task-history">
            {history.map((e) => (
              <li key={e.seq}>
                <time dateTime={e.created_at}>{new Date(e.created_at).toLocaleTimeString(locale)}</time>{" "}
                {e.actor_id ? t("{event} by {name}", { event: eventLabel(e.type), name: session.name(e.actor_id) }) : eventLabel(e.type)}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// TaskOutput shows what a task gave back, read through its output schema:
// each field under its title, words with their line breaks, lists as
// lists, and anything else as JSON.
function TaskOutput({ schema, output }: { schema: JSONSchema | null; output: unknown }) {
  const fields = outputFields(schema, output);
  if (!fields) return <pre className="task-output-raw">{typeof output === "string" ? output : JSON.stringify(output, null, 2)}</pre>;
  if (!fields.length) return null;
  return (
    <dl className="task-output" aria-label={t("Output")}>
      {fields.map((f) => (
        <div key={f.key}>
          <dt>
            {f.label}
            {f.hint && <span className="task-output-hint">{f.hint}</span>}
          </dt>
          <dd>
            {f.kind === "text" ? (
              f.text
            ) : f.kind === "list" ? (
              <ul>
                {f.items.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            ) : (
              <pre>{f.json}</pre>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
